import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  bookConcierge,
  ConciergeSlot,
  getConciergeSlots,
} from "../../../services/onboardingService";

type ConciergeBookingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const formatSlotLabel = (slot: ConciergeSlot): string => {
  const startsAt = new Date(slot.starts_at);
  const endsAt = new Date(slot.ends_at);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return slot.starts_at;
  }

  const dateLabel = startsAt.toLocaleDateString(undefined, {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

  const startTime = startsAt.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const endTime = endsAt.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dateLabel} · ${startTime} – ${endTime}`;
};

const ConciergeBookingModal = ({ isOpen, onClose, onSuccess }: ConciergeBookingModalProps) => {
  const { t } = useTranslation();

  const [slots, setSlots] = useState<ConciergeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState("");

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);

    try {
      const response = await getConciergeSlots();
      setSlots(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("concierge.toast.loadSlotsError"));
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedSlotId("");
    loadSlots();
  }, [isOpen, loadSlots]);

  const handleConfirm = async () => {
    if (!selectedSlotId) {
      toast.error(t("concierge.validation.slotRequired"));
      return;
    }

    setSubmitting(true);

    try {
      await bookConcierge(Number(selectedSlotId));
      toast.success(t("concierge.toast.bookSuccess"));
      onSuccess();
    } catch (error: any) {
      toast.error(error?.data || t("concierge.toast.bookError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{t("concierge.modal.title")}</ModalHeader>
      <ModalBody>
        <p className="text-muted">{t("concierge.modal.description")}</p>

        {loadingSlots ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" />
          </div>
        ) : slots.length === 0 ? (
          <p className="text-muted mb-0">{t("concierge.modal.empty")}</p>
        ) : (
          <div className="d-flex flex-column gap-2">
            {slots.map((slot) => (
              <div key={slot.id} className="form-check card-radio">
                <Input
                  type="radio"
                  id={`concierge-slot-${slot.id}`}
                  name="concierge-slot"
                  className="form-check-input"
                  checked={selectedSlotId === String(slot.id)}
                  onChange={() => setSelectedSlotId(String(slot.id))}
                />
                <Label
                  className="form-check-label w-100"
                  htmlFor={`concierge-slot-${slot.id}`}
                >
                  <span className="d-block fw-medium">{formatSlotLabel(slot)}</span>
                  <span className="text-muted small">
                    {t("concierge.modal.duration", { minutes: slot.duration_minutes })}
                  </span>
                </Label>
              </div>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={submitting}>
          {t("concierge.modal.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={loadingSlots || slots.length === 0 || submitting}
        >
          {submitting ? <Spinner size="sm" className="me-1" /> : null}
          {t("concierge.modal.confirm")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConciergeBookingModal;
